// The single sanctioned imperative DOM decorator layer.
//
// Everything here mutates the DOM outside of React's control, so it is
// deliberately narrow in scope: it exists EXCLUSIVELY to fill tokens/placeholders
// that live inside CMS-authored rich text injected via
// `dangerouslySetInnerHTML`. React cannot see inside a `dangerouslySetInnerHTML`
// subtree, so a token an editor typed into rich text (`.promo_callout_RTF`, a
// review-count placeholder, an `.l10n-*` span) can only be resolved by walking
// the committed DOM after hydration.
//
// Anything React renders must own its own behavior in the component. Do NOT add
// decorators here for React-emitted markup — that is the imperative-widget
// pattern this layer replaces.
import * as t from "io-ts";
import React from "react";

import { getDocument } from "@thelabnyc/thelabui/src/utils/dom";

import { FORM_FIELD_HAS_ERRORS_SELECTOR } from "../forms/classNames";
import { RegExpFromString, check, codecFromEnum } from "../models/utils";
import { decorateElements } from "./react";
import { getPageSetting } from "./settings";

export const initPromoCalloutMessages = async (): Promise<void> => {
    await decorateElements(".promo_callout_RTF", (elems: HTMLElement[]) => {
        const promoCalloutMessage =
            getPageSetting("primary_promo_callout_message") || "";

        for (const elem of elems) {
            const replaceText = (el: Element) => {
                if (el.childElementCount > 0) {
                    replaceText(el.children[0]);
                } else {
                    el.innerHTML = promoCalloutMessage;
                }
            };

            if (promoCalloutMessage) {
                replaceText(elem);
            }
            // This workaround, utilizing setTimeout, helps reduce the visibility of the abrupt transition in the copy
            setTimeout(() => {
                elem.classList.add("visible");
            }, 200);
        }
    });
    await decorateElements(
        ".secondary_promo_callout_RTF",
        (elems: HTMLElement[]) => {
            const promoCalloutMessage =
                getPageSetting("secondary_promo_callout_message") || "";

            for (const elem of elems) {
                const replaceText = (el: Element) => {
                    if (el.childElementCount > 0) {
                        replaceText(el.children[0]);
                    } else {
                        el.innerHTML = promoCalloutMessage;
                    }
                };

                if (promoCalloutMessage) {
                    replaceText(elem);
                }
                // This workaround, utilizing setTimeout, helps reduce the visibility of the abrupt transition in the copy
                setTimeout(() => {
                    elem.classList.add("visible");
                }, 200);
            }
        },
    );
};

/**
 * Replace a literal `token` wherever it appears inside the rich-text subtree of
 * every current and future element matching `selector`. This preserves
 * the surrounding authored copy and swaps only the token -- the mechanism a CMS
 * editor gets when they type a bare placeholder string into rich text.
 *
 * `getReplacement()` is evaluated once per matched batch; a falsy result leaves
 * the token untouched (its authored fallback stays visible). `token` should be
 * a non-global `RegExp` so `.test()`/`.replace()` act on the same first match.
 */
export const initTokenReplace = async (
    selector: string,
    token: RegExp,
    getReplacement: () => string,
): Promise<void> => {
    await decorateElements(selector, (elems: HTMLElement[]) => {
        const replacement = getReplacement();
        if (!replacement) {
            return;
        }
        for (const elem of elems) {
            for (const child of Array.from(elem.getElementsByTagName("*"))) {
                if (token.test(child.innerHTML)) {
                    child.innerHTML = child.innerHTML.replace(
                        token,
                        replacement,
                    );
                }
            }
        }
    });
};

/**
 * Run the given rich-text decorators once, after the component mounts. Effects
 * are inherently post-hydration, so no explicit hydration guard is needed inside
 * the component. Intended to be called from a PageSkeleton so the sanctioned
 * decorators run exactly once per full-document page.
 */
export const useRichTextDecorators = (
    decorators: ReadonlyArray<() => void | Promise<void>>,
): void => {
    React.useEffect(() => {
        for (const decorate of decorators) {
            void decorate();
        }
        // Mount-only: the decorators subscribe to the document (via
        // decorateElements' MutationObserver) and must not re-subscribe on
        // re-render.
    }, []);
};

// Link rewriting. Editors author links inside CMS rich text (and react-modal
// portals mount links document-level), so this decorates every present and
// future `a[href]` per the page's io-ts-validated ruleset. The decorateElements
// MutationObserver is intentionally document-level and stays for the life of the
// page.

enum LinkRewriteAction {
    DO_NOTHING = "DO_NOTHING", // Do not change the link
    REMOVE_LINK = "REMOVE_LINK", // Remove the link entirely (but preserve it's content).
    TARGET_BLANK = "TARGET_BLANK", // Set the default link target to open in a new window
    TARGET_TOP = "TARGET_TOP", // Set the default link target the topmost browsing context
    TARGET_SELF = "TARGET_SELF", // Set the default link target the current browsing context
}

const LinkRewriteRule = t.interface({
    pattern: RegExpFromString,
    action: codecFromEnum("LinkRewriteAction", LinkRewriteAction),
});

const LinkRewriteRuleset = t.interface({
    rules: t.array(LinkRewriteRule),
});

type LinkRewriteRule = t.TypeOf<typeof LinkRewriteRule>;
type LinkRewriteRuleset = t.TypeOf<typeof LinkRewriteRuleset>;

const rewriteLinks = async (): Promise<void> => {
    const ruleset = getRewriteRuleset();

    // Add a rule to the beginning of the ruleset to make sure we always ignore
    // page anchor links.
    ruleset.rules.unshift({
        pattern: /^#/,
        action: LinkRewriteAction.DO_NOTHING,
    });

    // Add a fallback rule to the end of the ruleset, so that if a link doesn't
    // match any previous rules, we set its target to _top.
    ruleset.rules.push({
        pattern: /.*/,
        action: LinkRewriteAction.TARGET_TOP,
    });

    // Run our tweak code on all present and future links
    await decorateElements("a[href]", (elems: HTMLElement[]) => {
        for (const elem of elems) {
            if (elem instanceof HTMLAnchorElement) {
                configureLink(ruleset, elem);
            }
        }
    });
};

const getRewriteRuleset = (): LinkRewriteRuleset => {
    // Get link config from the page
    const rawRules = getPageSetting("link_rewrite_rules") || { rules: [] };
    try {
        return check(LinkRewriteRuleset.decode(rawRules));
    } catch {
        return {
            rules: [],
        };
    }
};

const configureLink = (
    ruleset: LinkRewriteRuleset,
    link: HTMLAnchorElement,
): HTMLElement => {
    const href = link.href;
    for (const rule of ruleset.rules) {
        // Check if this rule applies to this link. If not, continue on to
        // the next rule.
        if (!href.match(rule.pattern)) {
            continue;
        }
        // Apply the rule and stop processing.
        return applyRule(rule, link);
    }
    return link;
};

const applyRule = (
    rule: LinkRewriteRule,
    link: HTMLAnchorElement,
): HTMLElement => {
    switch (rule.action) {
        // Deactivate the link by replacing it with a span.
        case LinkRewriteAction.REMOVE_LINK: {
            const document = getDocument();
            if (!document) {
                return link;
            }
            // Create a span
            const span = document.createElement("span");
            span.id = link.id;
            span.className = link.className;
            // Move all the link's children from the link to the span
            for (const child of Array.from(link.childNodes)) {
                span.appendChild(child);
            }
            // Replace the link with the span
            link.replaceWith(span);
            return span;
        }

        // If the link target isn't already set, set it to _blank
        case LinkRewriteAction.TARGET_BLANK:
            if (!link.target) {
                link.target = "_blank";
            }
            return link;

        // If the link target isn't already set, set it to _top
        case LinkRewriteAction.TARGET_TOP:
            if (!link.target) {
                link.target = "_top";
            }
            return link;

        // If the link target isn't already set, set it to _self
        case LinkRewriteAction.TARGET_SELF:
            if (!link.target) {
                link.target = "_self";
            }
            return link;

        // Don't change the link at all
        case LinkRewriteAction.DO_NOTHING:
        default:
            return link;
    }
};

/**
 * Apply the page's link-rewrite ruleset to every present and future `a[href]`,
 * once, after the component mounts. The decorateElements MutationObserver stays
 * for the life of the page to cover links added later (react-modal portals, CMS
 * content).
 */
export const useLinkRewrites = (): void => {
    React.useEffect(() => {
        void rewriteLinks();
    }, []);
};

/**
 * Focus the first `.form__field--has-errors` field's input on mount, matching
 * the server-rendered form-error affordance.
 */
export const useFocusFirstFormError = (): void => {
    React.useEffect(() => {
        void (async () => {
            const document = getDocument();
            if (!document) {
                return;
            }
            const fields = document.querySelectorAll<HTMLElement>(
                FORM_FIELD_HAS_ERRORS_SELECTOR,
            );
            if (fields.length > 0) {
                const { focusElement } = await import("./keyboardFocus");
                const fieldInput = fields[0].querySelector<HTMLElement>(
                    "input, select, textarea",
                );
                if (fieldInput) {
                    focusElement(fieldInput);
                }
            }
        })();
    }, []);
};
